//
// AHT Weight Management — Mysimba® (naltrexone/bupropion) shared screening downstream.
// Single source of truth for the post-face-to-face steps: PGD fresh checks,
// interaction checks, cautions review, supply attestation, and the shared
// outcomes. The BP/pulse measured-and-recorded requirement is enforced by the
// PGD inclusion check "inc-bp-measured-recorded" (exclude_on_no) inside
// PGD_FRESH_CHECKS, so there is no separate inline gate node here.
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.

import type { OutcomeNode, PathwayNode } from "../../../types/pathway.types";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

export const PGD_FRESH_CHECKS = "mys-init-pgd-fresh-checks";
export const Q4_INT_MAOI    = "mys-init-int1-maoi";
export const Q5_INT_OPIOID  = "mys-init-int2-opioid";
export const Q6_INT_CYP2D6  = "mys-init-int3-cyp2d6";
export const Q7_INT_SEIZURE = "mys-init-int4-seizure-threshold";
export const Q8_INT_CYP2B6  = "mys-init-int5-cyp2b6-inducer";
export const Q9_CAUTIONS    = "mys-init-cautions";
export const Q10_ATTEST     = "mys-init-dose-confirm";

// ─────────────────────────────────────────────────────────────────────────────
// Outcome IDs
// ─────────────────────────────────────────────────────────────────────────────

export const OUT_NO_F2F        = "OUT_CANNOT_NOT_F2F";
export const OUT_CANNOT_SUPPLY = "OUT_CANNOT_SUPPLY";
export const OUT_SUPPLY        = "OUT_SUPPLY";

// ─────────────────────────────────────────────────────────────────────────────
// Shared downstream nodes — spread into a pathway's nodes array after the
// confirmation / exclusion-question stage. The first node is PGD_FRESH_CHECKS.
// ─────────────────────────────────────────────────────────────────────────────

export const mysimbaScreeningDownstreamNodes: PathwayNode[] = [
  // ───────────────────────────────────────────────────────────────────────────
  // Step 3b — PGD inclusion / exclusion checks
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: PGD_FRESH_CHECKS,
    type: "pgd_exclusion_check",
    variant: "sequential",
    pgdIdOverride: "pgd-aht-wt-mysimba",
    anyTriggered: { outcomeId: OUT_CANNOT_SUPPLY },
    allCleared: Q4_INT_MAOI,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 4 — Interaction checks
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: Q4_INT_MAOI,
    type: "condition_route",
    conditions: [],
    default: Q5_INT_OPIOID,
  },

  {
    id: Q5_INT_OPIOID,
    type: "condition_route",
    conditions: [],
    default: Q6_INT_CYP2D6,
  },

  {
    id: Q6_INT_CYP2D6,
    type: "yes_no",
    text: "Is the patient taking CYP2D6 substrates (SSRIs, tricyclic antidepressants, antipsychotics, metoprolol, propafenone, or flecainide)?",
    hint: "Bupropion inhibits CYP2D6 — initiate at lower dose; therapeutic drug monitoring for narrow-index drugs.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document any anticipated dose adjustments and monitoring plan for CYP2D6 substrates",
      acknowledgementText: "Patient and prescriber have been informed of potential drug interaction and monitoring requirements",
    },
    yes: Q7_INT_SEIZURE,
    no: Q7_INT_SEIZURE,
    autoAnswer: { when: { hasMedication: ["metoprolol", "propafenone", "flecainide"] }, value: true },
  },

  {
    id: Q7_INT_SEIZURE,
    type: "yes_no",
    text: "Is the patient taking drugs that lower the seizure threshold (antipsychotics, antidepressants, tramadol, theophylline, quinolones, steroids, or antimalarials)?",
    hint: "Increased seizure risk.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document the seizure risk assessment and monitoring plan",
      acknowledgementText: "Patient has been counselled on increased seizure risk and understands the monitoring plan",
    },
    yes: Q8_INT_CYP2B6,
    no: Q8_INT_CYP2B6,
    autoAnswer: { when: { hasMedication: ["tramadol", "theophylline"] }, value: true },
  },

  {
    id: Q8_INT_CYP2B6,
    type: "yes_no",
    text: "Is the patient taking CYP2B6 enzyme inducers (carbamazepine, phenytoin, ritonavir, or efavirenz)?",
    hint: "May significantly reduce bupropion exposure.",
    managementPlan: {
      triggeredByAnswer: "yes",
      planPrompt: "Document any anticipated bupropion dose adjustments and monitoring plan",
      acknowledgementText: "Patient has been informed of potential reduced bupropion efficacy and monitoring requirements",
    },
    yes: Q9_CAUTIONS,
    no: Q9_CAUTIONS,
    autoAnswer: { when: { hasMedication: ["carbamazepine", "phenytoin", "ritonavir", "efavirenz"] }, value: true },
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 5 — Cautions review
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: Q9_CAUTIONS,
    type: "flag",
    variant: "warn",
    message:
      "Step 4 — Review cautions before supply:\n" +
      "• BP and pulse: measure before initiation, after each dose escalation, and at every review\n" +
      "• Seizure risk (~0.1%) dose-related — predisposing factors include head trauma, excessive alcohol, cocaine/stimulants, drugs lowering seizure threshold\n" +
      "• Suicidality: monitor closely for clinical worsening, suicidal ideation, unusual behavioural changes — especially first weeks and at dose changes\n" +
      "• Opioid blockade: naltrexone blocks opioid receptors — patients will not receive full pain relief; warn explicitly; advise to carry patient card\n" +
      "• Attempting to overcome opioid blockade with large opioid doses is dangerous and potentially fatal\n" +
      "• Serotonin syndrome risk with SSRIs/SNRIs\n" +
      "• Hepatotoxicity: drug-induced liver injury reported — discontinue if suspected\n" +
      "• Alcohol: minimise or avoid throughout treatment",
    next: Q10_ATTEST,
  },

  // ───────────────────────────────────────────────────────────────────────────
  // Step 6 — Supply attestation
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: Q10_ATTEST,
    type: "attestation",
    text: "Step 5 — Confirm supply of Mysimba® week 1 escalation pack",
    attestationStatement:
      "I confirm that I have:\n" +
      "• Supplied 7 × Mysimba® 8mg/90mg prolonged-release tablets (week 1 escalation dose)\n" +
      "• Supplied in original packaging with PIL and patient card\n" +
      "• Confirmed tablets must be swallowed whole — not cut, chewed, or crushed\n" +
      "• Provided written dose escalation schedule\n" +
      "• Advised patient to carry patient card AT ALL TIMES — opioid blockade warning\n" +
      "• Warned: will not receive full pain relief from opioids — tell ALL clinicians, dentists, paramedics\n" +
      "• Warned: attempting to override opioid blockade can be fatal\n" +
      "• BP and pulse recorded before supply\n" +
      "• Recorded batch number and expiry date\n" +
      "• Advised patient to inform GP on initiation",
    conclusions: {
      optionA: { label: "Confirm and supply", next: { outcomeId: OUT_SUPPLY } },
      optionB: { label: "Do not supply",      next: { outcomeId: OUT_CANNOT_SUPPLY } },
    },
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Shared outcomes — used by both the initial supply and re-screen pathways.
// ─────────────────────────────────────────────────────────────────────────────

export const mysimbaScreeningOutcomes: Record<string, OutcomeNode> = {
  [OUT_NO_F2F]: {
    id: OUT_NO_F2F,
    type: "outcome",
    outcomeType: "cannot_supply",
    title: "Cannot supply — face-to-face consultation required",
    reason:
      "Initial supply under this PGD requires the patient to attend in person at AHT registered premises.",
    safetyNetting: [
      "Ask the patient to book an in-person appointment to proceed with initial supply.",
    ],
  },
  [OUT_CANNOT_SUPPLY]: {
    id: OUT_CANNOT_SUPPLY,
    type: "outcome",
    outcomeType: "cannot_supply",
    title: "Cannot supply — Mysimba® (naltrexone/bupropion)",
    reason:
      "An inclusion criterion was not met, an exclusion criterion was triggered, or a contraindicated drug interaction was identified. Supply cannot proceed under this PGD.",
    safetyNetting: [
      "Discuss the reason supply cannot proceed with the patient.",
      "Consider referral to prescriber or GP if clinically appropriate.",
    ],
  },
  [OUT_SUPPLY]: {
    id: OUT_SUPPLY,
    type: "outcome",
    outcomeType: "supply",
    title: "Supply complete — Mysimba® week 1 (initial supply)",
    reason:
      "All inclusion criteria met, no exclusion criteria triggered, and supply attestation confirmed.",
    drugRef: "mysimba",
    safetyNetting: [
      "Return in 1 week to review tolerance and proceed to dose escalation.",
      "Carry patient card at all times — opioid blockade warning.",
      "Do not drive or operate machinery if dizziness, drowsiness, or seizures occur.",
      "Avoid alcohol throughout treatment.",
      "Report thoughts of self-harm, suicide, or unusual mood changes immediately.",
      "Seek urgent help if seizure occurs — STOP Mysimba®.",
    ],
  },
};
